import { memo, useEffect, useState, type FC, type ReactElement } from 'react';

// Components
import { Navbar } from './components/Navbar';
import { Library } from './components/Library';
import PlayingBar from './components/PlayingBar';
import { PlayingNow } from './components/NowPlaying';
import { LanguageModal } from '../Modals/LanguageModal';
import { LibraryDrawer } from '../Drawers/LibraryDrawer';
import { PlayingNowDrawer } from '../Drawers/PlayingNowDrawer';
import { EditPlaylistModal } from '../Modals/EditPlaylistModal';
import { MobileQueueModal } from './components/PlayingBar/MobileQueueModal';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { FullScreenPlayer } from '../FullScreen';
import MiniPlayer from '../MiniPlayer';
// Interfaces

// Redux
import { useAppDispatch, useAppSelector } from '../../store/store';
import { spotifyActions } from '../../store/slices/spotify';
import { uiActions } from '../../store/slices/ui';

import useIsMobile from '../../utils/isMobile';

export const AppLayout: FC<{ children: ReactElement }> = memo((props) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => !!state.auth.user);
  const rightLayoutOpen = useAppSelector((state) => !state.ui.queueCollapsed || !state.ui.devicesCollapsed || !state.ui.detailsCollapsed);
  const fullscreenNowPlaying = useAppSelector((state) => state.ui.fullscreenNowPlaying);
  const libraryCollapsed = useAppSelector((state) => state.ui.libraryCollapsed);
  const hasState = useAppSelector((state) => !!state.spotify.state);
  const activeOnOtherDevice = useAppSelector((state) => {
    const deviceId = state.spotify.deviceId;
    const activeDeviceId = state.spotify.activeDevice;
    return deviceId && activeDeviceId && deviceId !== activeDeviceId;
  });

  const [isTablet, setIsTablet] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerWidth;
      if (vh < 950) {
        dispatch(uiActions.collapseLibrary());
        setIsTablet(true);
      } else {
        setIsTablet(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    if (user) dispatch(spotifyActions.fetchDevices());
  }, [user, dispatch]);

  const [shouldRenderFullScreen, setShouldRenderFullScreen] = useState(false);

  useEffect(() => {
    if (fullscreenNowPlaying) {
      setShouldRenderFullScreen(true);
    } else {
      const timeout = setTimeout(() => {
        setShouldRenderFullScreen(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [fullscreenNowPlaying]);

  useEffect(() => {
    // This forcefully resets the layout's scroll position if it was hijacked by scrollIntoView
    const row = document.getElementById('main-layout-row');
    if (row && row.scrollTop > 0) row.scrollTop = 0;
  }, []);

  return (
    <>
      {/* Modals & Drawers */}
      <LanguageModal />
      <LibraryDrawer />
      <PlayingNowDrawer />
      <EditPlaylistModal />
      <MobileQueueModal />
      <MiniPlayer />

      {/* Main Component */}
      <div className='main-container'>
        <div
          id='main-layout-row'
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflow: 'hidden',
            height: isMobile
              ? '100vh'
              : `calc(100vh - ${activeOnOtherDevice ? '131' : '95'}px)`,
          }}
        >
          <div style={{ width: '100%' }}>
            <Navbar />
          </div>

          <div
            style={{
              maxHeight: activeOnOtherDevice ? `calc(100vh - 175px)` : undefined,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}
          >
            <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', flex: 1 }}>
              <div 
                style={{
                  width: '100%',
                  height: '100%',
                  opacity: fullscreenNowPlaying ? 0 : 1,
                  pointerEvents: fullscreenNowPlaying ? 'none' : 'auto',
                  transition: 'opacity 0.4s cubic-bezier(0.2, 0, 0, 1)'
                }}
              >
                <PanelGroup direction='horizontal' autoSaveId='persistence-outer-v2' style={{ height: '100%' }}>
                  <Panel
                    id='left'
                    order={1}
                    className={`mobile-hidden ${libraryCollapsed ? 'collapsed-library-panel' : 'expanded-library-panel'}`}
                    minSize={isTablet ? 10 : libraryCollapsed ? 7 : 22}
                    maxSize={isTablet ? 10 : libraryCollapsed ? 8 : 28}
                    defaultSize={isTablet ? 10 : libraryCollapsed ? 7 : 22}
                    style={{ borderRadius: 8 }}
                  >
                    <div style={{ height: '100%' }}>
                      <Library />
                    </div>
                  </Panel>

                  {!isMobile ? <PanelResizeHandle className='resize-handler' /> : null}
                  <Panel id='right-wrapper' order={2} style={{ borderRadius: 8 }}>
                    <PanelGroup direction='horizontal' autoSaveId='persistence-inner-v2' style={{ height: '100%' }}>
                      <Panel id='center' order={1} style={{ borderRadius: 8 }}>
                        {/* Home | Playlists */}
                        {props.children}
                      </Panel>

                      {!isMobile && !isTablet && hasState ? (
                        <PanelResizeHandle className='resize-handler' />
                      ) : null}

                      {!isMobile && hasState ? (
                        <Panel
                          order={2}
                          className={!rightLayoutOpen ? "collapsed-now-playing-panel" : "expanded-now-playing-panel"}
                          minSize={isTablet ? 20 : 18}
                          maxSize={isTablet ? 35 : 28}
                          defaultSize={isTablet ? 25 : 20}
                          id='details-section'
                          style={{
                            borderRadius: 8,
                            overflow: 'visible',
                            minWidth: !rightLayoutOpen ? 72 : 280,
                            maxWidth: !rightLayoutOpen ? 72 : 420,
                          }}
                        >
                          <div style={{ height: '100%', minWidth: 0 }}>
                            <PlayingNow />
                          </div>
                        </Panel>
                      ) : null}
                    </PanelGroup>
                  </Panel>
                </PanelGroup>
              </div>

              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 100,
                  opacity: fullscreenNowPlaying ? 1 : 0,
                  transform: fullscreenNowPlaying ? 'translateY(0) scale(1)' : 'translateY(80px) scale(0.95)',
                  pointerEvents: fullscreenNowPlaying ? 'auto' : 'none',
                  transition: 'opacity 0.4s cubic-bezier(0.2, 0, 0, 1), transform 0.5s cubic-bezier(0.2, 0, 0, 1)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#121212'
                }}
              >
                {shouldRenderFullScreen && <FullScreenPlayer onExit={async () => { dispatch(uiActions.closeFullscreenNowPlaying()); }} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {<footer>{user ? <PlayingBar /> : null}</footer>}
    </>
  );
});

AppLayout.displayName = 'AppLayout';

// Trigger rebuild

// Trigger rebuild 2
